// Unified data layer for QuickDrop Go.
// Uses Firebase Firestore + Storage when configured; otherwise a localStorage
// fallback (seeded with defaults) so the app is fully usable and testable now.
// The public API is identical in both modes — no code changes needed once you
// paste your Firebase config.
import { isFirebaseConfigured, db, storage } from "./firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  defaultServices,
  defaultCategories,
  defaultContent,
  defaultSettings,
} from "./defaults";

const LS = "qdg::";
const uid = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

/* ------------------------- local fallback helpers ------------------------- */
const lsGet = (k, fb) => {
  try {
    const v = localStorage.getItem(LS + k);
    return v ? JSON.parse(v) : fb;
  } catch {
    return fb;
  }
};
const lsSet = (k, v) => localStorage.setItem(LS + k, JSON.stringify(v));

function seedLocal() {
  if (!lsGet("__seeded")) {
    lsSet("services", defaultServices);
    lsSet("categories", defaultCategories);
    lsSet("content", defaultContent);
    lsSet("settings", defaultSettings);
    lsSet("partners", []);
    lsSet("riders", []);
    lsSet("contacts", []);
    lsSet("activities", []);
    lsSet("__seeded", true);
  }
}
if (typeof window !== "undefined") seedLocal();

/* --------------------------------- images --------------------------------- */
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export async function uploadImage(file, folder = "uploads") {
  if (!file) return "";
  if (isFirebaseConfigured) {
    const safe = `${Date.now()}-${(file.name || "img").replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    const r = ref(storage, `${folder}/${safe}`);
    await uploadBytes(r, file);
    return await getDownloadURL(r);
  }
  // Local fallback -> base64 data URL (migratable to Storage later).
  return await fileToDataUrl(file);
}

/* -------------------------------- activity -------------------------------- */
export async function logActivity(type, message) {
  const entry = { id: uid(), type, message, createdAt: new Date().toISOString() };
  if (isFirebaseConfigured) {
    await addDoc(collection(db, "activities"), {
      ...entry,
      createdAt: serverTimestamp(),
    });
    return;
  }
  const list = lsGet("activities", []);
  list.unshift(entry);
  lsSet("activities", list.slice(0, 50));
}

export async function listActivities(max = 10) {
  if (isFirebaseConfigured) {
    const snap = await getDocs(collection(db, "activities"));
    const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    arr.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
    return arr.slice(0, max);
  }
  return lsGet("activities", []).slice(0, max);
}

const tsToMs = (t) =>
  t?.toMillis ? t.toMillis() : t ? new Date(t).getTime() : 0;

/* ------------------------------ collections ------------------------------- */
export async function listCollection(name) {
  if (isFirebaseConfigured) {
    const snap = await getDocs(collection(db, name));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  return lsGet(name, []);
}

export async function listCollectionByCreatedAt(name, maxItems = 200) {
  if (isFirebaseConfigured) {
    try {
      const q = query(collection(db, name), orderBy("createdAt", "desc"), limit(maxItems));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch {
      const snap = await getDocs(collection(db, name));
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt));
      return items.slice(0, maxItems);
    }
  }

  const items = lsGet(name, []);
  return [...items]
    .sort((a, b) => tsToMs(b.createdAt) - tsToMs(a.createdAt))
    .slice(0, maxItems);
}

export async function createDoc(name, data) {
  const payload = { ...data, createdAt: new Date().toISOString() };
  if (isFirebaseConfigured) {
    const r = await addDoc(collection(db, name), {
      ...data,
      createdAt: serverTimestamp(),
    });
    return { id: r.id, ...payload };
  }
  const list = lsGet(name, []);
  const item = { id: uid(), ...payload };
  list.unshift(item);
  lsSet(name, list);
  return item;
}

export async function updateDocById(name, id, patch) {
  if (isFirebaseConfigured) {
    await updateDoc(doc(db, name, id), patch);
    return;
  }
  const list = lsGet(name, []);
  const idx = list.findIndex((x) => x.id === id);
  if (idx > -1) {
    list[idx] = { ...list[idx], ...patch };
    lsSet(name, list);
  }
}

export async function deleteDocById(name, id) {
  if (isFirebaseConfigured) {
    await deleteDoc(doc(db, name, id));
    return;
  }
  lsSet(
    name,
    lsGet(name, []).filter((x) => x.id !== id)
  );
}

/* -------------------------------- singletons ------------------------------ */
const singletonDefaults = { content: defaultContent, settings: defaultSettings };

export async function getSingleton(name) {
  if (isFirebaseConfigured) {
    const snap = await getDoc(doc(db, name, "site"));
    return snap.exists() ? snap.data() : singletonDefaults[name];
  }
  return lsGet(name, singletonDefaults[name]);
}

export async function setSingleton(name, data) {
  if (isFirebaseConfigured) {
    await setDoc(doc(db, name, "site"), data, { merge: true });
    return;
  }
  lsSet(name, { ...lsGet(name, singletonDefaults[name]), ...data });
}

/* ---------------------- public reads with safe fallback ------------------- */
export async function getPublicServices() {
  try {
    const s = await listCollection("services");
    const arr = s.length ? s : defaultServices;
    return arr.filter((x) => x.enabled !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch {
    return defaultServices;
  }
}

export async function getPublicCategories() {
  try {
    const c = await listCollection("categories");
    const arr = c.length ? c : defaultCategories;
    return arr.filter((x) => x.enabled !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch {
    return defaultCategories;
  }
}

export async function getPublicContent() {
  try {
    return { ...defaultContent, ...(await getSingleton("content")) };
  } catch {
    return defaultContent;
  }
}

export async function getPublicSettings() {
  try {
    return { ...defaultSettings, ...(await getSingleton("settings")) };
  } catch {
    return defaultSettings;
  }
}
